import "./styles.css";
import "./lib/theme";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { i18n } from "./lib/i18n";
import { queryClient, setUnauthorizedHandler } from "./lib/query";
import { pageTransition } from "./lib/page-transition";
import { installErrorReporting, reportError } from "./lib/report-errors";
import { routeTree } from "./routeTree.gen";

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // Page changes go through the View Transitions API: the browser snapshots both pages and the
  // CSS animates the content only (styles.css). Browsers without it change pages instantly.
  defaultViewTransition: {
    types: ({ fromLocation, toLocation }) => {
      const type = pageTransition(fromLocation?.pathname, toLocation.pathname);
      return type ? [type] : false;
    },
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

setUnauthorizedHandler(() => {
  queryClient.removeQueries({ queryKey: ["me"] });
  if (router.state.location.pathname !== "/login") {
    void router.navigate({ to: "/login", search: { redirect: router.state.location.href } });
  }
});

installErrorReporting();

createRoot(document.getElementById("root")!, {
  // Rendering errors caught by an error screen are still bugs: report them too.
  onCaughtError: (error) => {
    console.error(error);
    reportError(error);
  },
  onUncaughtError: (error) => {
    console.error(error);
    reportError(error);
  },
}).render(
  <StrictMode>
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </I18nextProvider>
  </StrictMode>,
);
